/**
 * The caller's own /me calls (pending codes, feedback, and the B-7202
 * post-export dialog opt-out + summary email), spread into BackendAPI. Split
 * out of api.ts (god-file ratchet). Takes the configured axios instance so the
 * interceptors and base URL are the same.
 */
import type { AxiosInstance } from 'axios';
import type { ExportPreferences, ExportSummarySteps, ImportQuestion } from '../types/exportSummary';

export const meApi = (api: AxiosInstance) => ({
  // In-app recovery for code emails that were lost/dismissed: the caller's
  // unredeemed signup/purchase codes (never feedback rewards).
  getPendingCodes: () =>
    api.get('/me/pending-codes') as Promise<
      Array<{ code: string; credits: number; source: string; created_at: string | null }>
    >,
  submitFeedback: (payload: { body: string; rating?: number; would_pay?: string; run_ref?: string }) =>
    api.post('/me/feedback', payload) as Promise<{ status: string }>,

  // --- Post-export dialog opt-out + summary email (B-7202) ---
  getExportPreferences: (): Promise<ExportPreferences> => api.get('/me/export-preferences'),
  setExportPreferences: (prefs: ExportPreferences): Promise<ExportPreferences> =>
    api.put('/me/export-preferences', prefs),
  /** PRP-MCP-5 (D2): the one stored export disclaimer, shown next to Download ESX. */
  getExportDisclaimer: (): Promise<{ text: string }> => api.get('/me/export-disclaimer'),
  getExportSummary: (parseHash: string): Promise<ExportSummarySteps> =>
    api.get(`/me/exports/${encodeURIComponent(parseHash)}/summary`),
  /** Emails the export's steps + review list. The server computes every number;
   *  the body only says which export this was and the viewer's time zone. */
  sendExportSummaryEmail: (parseHash: string, exportRef: string, timezone?: string) =>
    api.post(`/me/exports/${encodeURIComponent(parseHash)}/summary-email`, {
      export_ref: exportRef,
      timezone,
    }) as Promise<{ status: string }>,
  /** PRP-MCP-8: the locked-export panel was shown (view) or a link clicked (click + target). */
  recordExportLockedEvent: (parseHash: string, event: 'view' | 'click', target?: 'trial' | 'subscribe') =>
    api.post(`/me/exports/${encodeURIComponent(parseHash)}/locked-event`, { event, target }) as Promise<{
      status: string;
    }>,
  /** PRP-MCP-8: the optional "Did this import cleanly into Xactimate?" question after an export. */
  getImportQuestion: (): Promise<ImportQuestion> => api.get('/me/import-question'),
  sendImportAnswer: (parseHash: string, answer: string, exportRef?: string) =>
    api.post(`/me/exports/${encodeURIComponent(parseHash)}/import-answer`, {
      answer,
      export_ref: exportRef,
    }) as Promise<{ status: string }>,
});
